import {all, call, delay, put, takeLatest} from 'redux-saga/effects';
import {actions, ActionType} from './actions';
import {
  getBanners,
  getContentLike,
  getRecommendContents,
  getViewerComment,
  getViewerContentAndEpisode,
  getViewerLink,
  getViewerResources,
  getContentDecorator,
} from './apiGet';
import {Page} from '../../common/constant/page';
import {getDeviceId} from '../../common/function';
import {selectT} from '../../common/i18n';
import callApi, {callApiList} from '../../common/util/api';
import {makeFetchSaga} from '../../common/util/fetch';
import {PageInfo, redirectTo} from '../../common/util/PageRouter';
import {typedSelect} from '../../common/util/saga';
import {RECENT_ITEM_LOCAL_PREFIX} from '../../commonSub/constant';
import {
  decryptImages,
  getCampaignParams,
  getIsProhibitedEpisode,
  getLocalRecentItemsV4,
  getViewerResourceParams,
  setLocalRecentItemsV4,
} from '../../commonSub/function';
import {getOriginalProduct} from '../../commonSub/state/apiGet';
import {IViewerEnd} from '../type';

function* fetchViewerEpisode(
  action: ReturnType<typeof actions.fetchViewerEpisode>
) {
  const {episodeId, sid, res, req} = action.payload;
  const {success, data} = yield call(callApi, {
    url: `/episode/v2/episodes/${episodeId}`,
    processApiErrorCallback: error => {
      if (getIsProhibitedEpisode(error.errorType)) {
        redirectTo({pageInfo: Page.Home, res});
        return true;
      }
      return false;
    },
  });

  if (success && data) {
    const {content, episode} = getViewerContentAndEpisode(data);
    if (sid !== episode.sid) {
      const pageInfo: PageInfo = {
        page: Page.Viewer,
        id: episodeId,
        sid: episode.sid,
      };
      redirectTo({pageInfo, res, extraQuery: getCampaignParams(req?.url)});
    }
    yield put(actions.setViewerContentAndEpisode(content, episode));
  }
}

function* fetchResources(action: ReturnType<typeof actions.fetchResources>) {
  const {episodeId, contentId, initialPage} = action.payload;
  const {type, nonce, timestamp} = yield call(getViewerResourceParams);
  const {success, data} = yield call(callApi, {
    method: 'POST',
    url: `/episode/v1/views/viewer/episodes/${episodeId}/media-resources`,
    data: {
      id: episodeId,
      type,
      nonce,
      timestamp,
      download: false,
      webAppId: getDeviceId(),
    },
    // use ticket에서 이미 팝업이 뜨기 때문에 resource 요청시에는 제외한다.
    processApiErrorCallback: error => error.errorType === 'UNDER_AGE_GRADE',
  });

  if (success) {
    const resources = getViewerResources({
      episodeId,
      media: data.media,
      nonce,
      timestamp,
    });
    yield put(actions.setResources(resources));
    yield call(saveRecentContent, contentId);
    yield call(decryptImages, {
      initialPage,
      episodeId,
      onFetchImgFinishedAction: ActionType.OnFetchImageFinished,
      encUrls: resources.images.map(image => image.encUrl),
      decryptInfo: resources.decryptInfo,
      actions,
    });
  }
}

function* saveRecentContent(contentId: number) {
  const _episode = yield* typedSelect(state => state.viewer.episode);
  if (!_episode) return;

  const episode = {
    id: _episode.id,
    sid: _episode.sid,
    title: _episode.title,
    type: _episode.type,
  };
  const readDateTime = Date.now();
  const localData = getLocalRecentItemsV4();
  const localId = `${RECENT_ITEM_LOCAL_PREFIX}${contentId}`;
  const duplicateIndex = localData.findIndex(item => item.id === localId);
  if (duplicateIndex === -1) {
    const {success, data} = yield call(callApi, {
      url: `/decorator/v1/decorator/compact-contents/${contentId}`,
    });
    if (success && data) {
      localData.unshift({
        id: localId,
        episode,
        content: getContentDecorator(data),
        readDateTime,
      });
    }
  } else {
    const duplicate = localData.splice(duplicateIndex, 1)[0];
    duplicate.readDateTime = readDateTime;
    duplicate.episode = episode;
    localData.unshift(duplicate);
  }
  // TODO:
  //   안본 작품 뷰어 진입 > 보관함 > 감상항 목록이 보이지 않는 문제가 있다.
  //   변경된 값을 감지할 수 있도록 useLocalStorage 사용하자
  setLocalRecentItemsV4(localData);
}

function* fetchViewerData(action: ReturnType<typeof actions.fetchViewerData>) {
  const {contentId, episodeId} = action.payload;

  const apiList = [
    {
      url: `/episode/v1/views/viewer/episodes/${episodeId}/links`,
    },
    {
      url: '/comment/v1/comments/best-comments',
      params: {episodeId},
    },
    {
      url: '/section/v1/view/advertisements/viewer',
      params: {contentId},
    },
    {
      url: '/decorator/v2/view/recommendations/most-similar',
      params: {contentId},
    },
    {
      url: `/decorator/v1/view/connections/page-novel?contentId=${contentId}`,
    },
    {
      url: `/popularity/v1/reviews/episode/${episodeId}`,
      params: {contentId},
    },
  ];

  const isLogin = yield* typedSelect(state => !!state.auth.user);
  if (isLogin) {
    apiList.push({
      url: '/popularity/v1/my-review',
      params: {episodeId},
    });
  }

  const {data, meta} = yield call(callApiList, apiList);
  const t = yield* selectT();
  const {prevEpisode, nextEpisode, curEpisodeOffset} = getViewerLink(
    t,
    data[0]
  );
  const viewerEnd: IViewerEnd = {
    viewerComment: getViewerComment({t, data: data[1], meta: meta[1]}),
    ...getBanners(data[2]),
    recommendContents: getRecommendContents(data[3]),
    originalProduct: data[4] && getOriginalProduct(data[4]),
    episodeLike: getContentLike(data[5], data[6]),
  };

  yield put(
    actions.setViewerData({
      prevEpisode,
      nextEpisode,
      viewerEnd,
      curEpisodeOffset,
    })
  );
}

function* fetchLikeEpisode(
  action: ReturnType<typeof actions.fetchLikeEpisode>
) {
  const episodeLike = yield* typedSelect(
    state => state.viewer.viewerEnd?.episodeLike
  );
  if (!episodeLike) return;

  const {likeCount: newLikeCount, rollbackCount} = action.payload;
  const diff = newLikeCount - episodeLike.myLikeCount;
  const newEpisodeLike = {
    totalLikeCount: episodeLike.totalLikeCount + diff,
    myLikeCount: newLikeCount,
  };
  yield put(
    actions.setPropertyValues('viewerEnd', {episodeLike: newEpisodeLike})
  );

  yield delay(500); // api 요청은 debouncing 한다..

  const {success} = yield call(callApi, {
    method: 'PUT',
    url: '/popularity/v1/reviews',
    data: action.payload,
  });

  if (!success) {
    const diff = newLikeCount - rollbackCount;
    yield put(
      actions.setPropertyValues('viewerEnd', {
        episodeLike: {
          totalLikeCount: newEpisodeLike.totalLikeCount - diff,
          myLikeCount: newEpisodeLike.myLikeCount - diff,
        },
      })
    );
  }
}

export default function* saga() {
  yield all([
    takeLatest(
      ActionType.FetchViewerEpisode,
      makeFetchSaga({fetchSaga: fetchViewerEpisode, canCache: true})
    ),
    takeLatest(
      ActionType.FetchResources,
      makeFetchSaga({fetchSaga: fetchResources, canCache: false})
    ),
    takeLatest(
      ActionType.FetchViewerData,
      makeFetchSaga({fetchSaga: fetchViewerData, canCache: true})
    ),
    takeLatest(
      ActionType.FetchLikeEpisode,
      makeFetchSaga({fetchSaga: fetchLikeEpisode, canCache: false})
    ),
  ]);
}
